/**
 * ProjectLlmSelect — the LLM service a project's features run on.
 *
 * Beta-user-only, and the caller decides that: this renders nothing unless
 * `visible` is true. The server enforces the same rule, so hiding the
 * control is a courtesy rather than the security boundary.
 *
 * The options come from the deployment (GET /llm/options), not a hard-coded
 * list, so a service that ops has not configured is never offered. The first
 * entry is always "Default (<service>)", which stores null and lets the
 * project follow the platform default as it changes.
 */

import React, { useEffect, useState } from 'react';
import { Text, Select } from '@ui-library';
import { getLlmOptions, LlmOptions, LlmServiceOption } from '../../services/projectLlm';

/** The value the Select uses for "use the platform default". */
export const LLM_DEFAULT_VALUE = '__default__';

/**
 * What to call a service in the UI.
 *
 * The catalogue names each folder after the model family, which is right for
 * config but reads oddly in a vendor list: `claude` is the model, Anthropic
 * is the vendor. Anything not listed falls back to the server's own label,
 * so a service added later still appears without a frontend change.
 */
const SERVICE_NAMES: Record<string, string> = {
  claude: 'Anthropic',
  openai: 'OpenAI',
  gemini: 'Gemini',
  glm: 'GLM',
  kimi: 'Kimi',
  deepseek: 'DeepSeek',
};

/**
 * The channel, shortened to the one word that distinguishes it.
 *
 * The server's labels are written for an ops screen ("Anthropic API
 * (direct)", "Azure AI Foundry"), and pasting them in gives nested
 * parentheses — "Claude (Anthropic API (direct))". Here the service is
 * already named, so only the route needs saying.
 */
const CHANNEL_NAMES: Record<string, string> = {
  anthropic_api: 'API',
  openai_api: 'API',
  gemini_api: 'API',
  deepseek_api: 'API',
  kimi_api: 'API',
  zhipu_api: 'API',
  azure: 'Azure',
  azure_foundry: 'Foundry',
  bedrock: 'Bedrock',
  vertex: 'Vertex',
};

const serviceName = (id: string, fallback: string) => SERVICE_NAMES[id] || fallback;

/** "Anthropic (API)" — or just "GLM" when the channel adds nothing. */
const optionLabel = (service: LlmServiceOption) => {
  const name = serviceName(service.id, service.label);
  const channel = CHANNEL_NAMES[service.channel];
  return channel ? `${name} (${channel})` : name;
};

interface ProjectLlmSelectProps {
  /** Rendered only when true — the caller's beta user check. */
  visible: boolean;
  /** The chosen service id, or null for the platform default. */
  value: string | null;
  onChange: (service: string | null) => void;
  disabled?: boolean;
  /** Shown when the project's stored service is no longer offered. */
  problem?: string | null;
  id?: string;
}

export const ProjectLlmSelect: React.FC<ProjectLlmSelectProps> = ({
  visible,
  value,
  onChange,
  disabled = false,
  problem,
  id = 'project-llm',
}) => {
  const [options, setOptions] = useState<LlmOptions | null>(null);
  const [loadError, setLoadError] = useState(false);

  // Only a beta user can see the control, so only a beta user fetches the options.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoadError(false);
    getLlmOptions()
      .then(data => {
        if (!cancelled) setOptions(data);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  if (!visible) return null;

  // Nothing to choose between is not worth a broken-looking control: the
  // project keeps whatever it has and follows the platform default.
  if (loadError || !options) return null;

  // "Default (Anthropic)": the server's label names the service the default
  // resolves to, which is the catalogue id, so it gets the same rename.
  const defaultService = options.services.find(s => s.id === options.default.service);
  const defaultName = serviceName(
    options.default.service,
    defaultService?.label || options.default.service
  );

  const selectOptions = [
    { label: `Default (${defaultName})`, value: LLM_DEFAULT_VALUE },
    ...options.services.map(service => ({
      label: optionLabel(service),
      value: service.id,
    })),
  ];

  // A stored service this deployment no longer offers is not in the list.
  // Keep it selectable rather than silently showing "Default", which would
  // misreport what the project actually runs on.
  const isKnown =
    value === null || options.services.some(service => service.id === value);
  if (!isKnown && value) {
    selectOptions.push({ label: `${serviceName(value, value)} (unavailable)`, value });
  }

  return (
    <div className="w-full">
      <Text variant="text-md" weight="semibold" color="primary" className="mb-3">
        LLM Service
      </Text>
      <Select
        id={id}
        name={id}
        options={selectOptions}
        value={value ?? LLM_DEFAULT_VALUE}
        onChange={selected =>
          onChange(selected === LLM_DEFAULT_VALUE ? null : String(selected))
        }
        disabled={disabled}
        variant={problem ? 'error' : 'default'}
      />
      <Text
        variant="text-xs"
        color={problem ? 'error' : 'tertiary'}
        className="mt-bg-v2-xs break-words block"
      >
        {problem || 'Every feature of this project runs on this LLM service.'}
      </Text>
    </div>
  );
};
